import {
  all, call, put, select, takeLatest,
} from '@redux-saga/core/effects';
import qs from 'qs';
import ReferralActionType from 'actions/referral/referralActionType';
import {
  changeLoadingState, setErrorMessage,
} from 'app/settings/slices';
import NetworkService from 'services/networkService';
import {
  saveReferral,
  saveReferralReferees,
  savePlayerReferralReferees,
  savePlayerReferrals,
  savePlayerReferralPeriods,
  saveReferralTransferredPeriods,
  saveReferralFailedPeriods,
  saveReferralAccumulatedPeriods,
} from 'actions/referral/referralActionCreator';
import enumTypes from 'constants/enumTypes';
import {
  selectPlayerReferralPeriods, selectReferralFailedPeriods, selectReferralTransferredPeriods, selectReferralAccumulatedPeriods,
} from 'selectors';
import { AppConstants } from 'constants/index';
import { isJSON } from 'utils/utils';
import { getError } from 'utils/helpers';

function* getReferral({ payload }) {
  try {
    const { referralId } = payload;
    const { DataCollector, Referral } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [DataCollector, Referral, referralId]);
    yield put(saveReferral(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err, true);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getReferralCommissionPeriods({ payload }) {
  try {
    const { referralId, params } = payload;
    const { periodStatus, referrerId } = params;
    const { DataCollector, Referral, CommissionDetails } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data: periods } = yield call(NetworkService.makeAPIGetRequest, [DataCollector, Referral, CommissionDetails, referralId], { params });
    const { referral } = yield select();

    let selectFunc = () => {};
    let saveFunc = () => {};
    switch (periodStatus) {
    case enumTypes.cashBackReferralPeriodStatus.Transferred: {
      selectFunc = selectReferralTransferredPeriods;
      saveFunc = saveReferralTransferredPeriods;
      break;
    }
    case enumTypes.cashBackReferralPeriodStatus.Failed: {
      selectFunc = selectReferralFailedPeriods;
      saveFunc = saveReferralFailedPeriods;
      break;
    }
    case enumTypes.cashBackReferralPeriodStatus.Accumulated: {
      selectFunc = selectReferralAccumulatedPeriods;
      saveFunc = saveReferralAccumulatedPeriods;
      break;
    }
    default: {
      selectFunc = selectPlayerReferralPeriods;
      saveFunc = savePlayerReferralPeriods;
    }
    }

    // no period case is player's bonus case
    const rowId = periodStatus ? referrerId : referralId;
    const data = selectFunc(referral);
    data[rowId] = periods;
    yield put(saveFunc(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getReferralReferees({ payload }) {
  try {
    const { batchId, params } = payload;
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    const { DataCollector, Referral, Referees } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [DataCollector, Referral, Referees, batchId], options);
    yield put(saveReferralReferees(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err, true);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerReferralReferees({ payload }) {
  try {
    const { playerId, params } = payload;
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    const { DataCollector, Referral, ByPlayerReferees } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [DataCollector, Referral, ByPlayerReferees, playerId], options);
    yield put(savePlayerReferralReferees(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerReferrals({ payload }) {
  try {
    const { playerId, params } = payload;
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    const { DataCollector, Referral, ByPlayer } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [DataCollector, Referral, ByPlayer, playerId], options);
    yield put(savePlayerReferrals(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

export default function* referralSaga() {
  yield all([
    takeLatest(ReferralActionType.GET_REFERRAL, getReferral),
    takeLatest(ReferralActionType.GET_REFERRAL_COMMISSION_PERIODS, getReferralCommissionPeriods),
    takeLatest(ReferralActionType.GET_REFERRAL_REFEREES, getReferralReferees),
    takeLatest(ReferralActionType.GET_PLAYER_REFERRAL_REFEREES, getPlayerReferralReferees),
    takeLatest(ReferralActionType.GET_PLAYER_REFERRALS, getPlayerReferrals),
  ]);
}
